import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import moment from "moment";

interface IToolsContext {
    recordCount: number
    search: ISearchRequest
    tools: ITool[]
    isLoading: boolean

    doSearch: (search: ISearchRequest) => void
    handleChangePage: (page: number) => void
    searchTools: (searchRequest: ISearchRequest) => Promise<void>
    deleteTool: (toolId: string) => Promise<void>
    handleChange: (newSearch: ISearchRequest) => void
}

export interface ITool {
    toolId: string,
    toolTypologyId: string,
    serialNumber: string,
    name: string,
    isAvailable: boolean,
    nextMaintenanceDate: moment.Moment,
    lastMaintenanceDate: moment.Moment,
    notes: string,
    usedByUserName: string,
    needMaintenance: boolean
}

interface ISearchRequest {
    searchText: string
    typologyId: string
    isAvailable: boolean
    needMaintenance: boolean
    pageNr: number
    pageSize: number
}

export let toolsContext = React.createContext({} as IToolsContext);

let { Provider } = toolsContext;

let ToolsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let history = useHistory();
    let location = useLocation();

    let [isLoading, setLoading] = useState(true);

    let [tools, setTools] = useState<ITool[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearchRequest>({
        searchText: '',
        typologyId: '',
        isAvailable: false,
        needMaintenance: false,
        pageNr: 0,
        pageSize: 15
    });


    useEffect(() => {
        if (location.state)
            processState(location.state);
    }, [location.state])

    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search } }} />


    let processState = (state: any) => {
        if (state.search) {
            setSearch(state.search);
            searchTools(state.search);
        }
    }

    let doSearch = (values: ISearchRequest) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        history.push(location.pathname, {
            search: request,
            selectedTools: [],
            allPageSelected: false
        });
    }

    let searchTools = async (searchRequest: ISearchRequest) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/Tool/SearchTools', searchRequest);
            setTools(response.data.items);
            setTotalRecords(response.data.recordCount);
        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, {
            search: request,
            selectedTools: [],
            allPageSelected: false
        });
    }

    let deleteTool = async (toolId: string) => {
        if (window.confirm("Vuoi eliminare questa Topologia?")) {
            try {
                await axios.delete(`/api/Tool/DeleteTool/${toolId}`)
                setTools(tools.filter(tool => tool.toolId !== toolId));
                setTotalRecords(recordCount - 1);
                enqueueSnackbar("Tool deleted", { variant: 'success' });
            } catch (error) { }
        }
    }

    let handleChange = (newSearch: ISearchRequest) => {
        setSearch(newSearch);
    }

    return (
        <Provider value={{
            recordCount,
            search,
            tools,
            isLoading,

            doSearch,
            handleChangePage,
            searchTools,
            deleteTool,
            handleChange
        }}>
            {children}
        </Provider>
    );
};

export default ToolsProvider;